import 'src/screen/Protected/ApiKeysScreen/ApiKeysScreen.scss';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Modal, Paper, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Box, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Button from 'src/component/UI/Button';
import ApiKeysList from './ApiKeysList';
import { createApiKey, clearApiKeyToken } from 'src/module/apiKeys/action';
import { getApiKeyCreationLoading, getNewApiKeyToken } from 'src/module/apiKeys/selector';
import toast from 'src/utils/toast';
import { getPermissions } from 'src/module/permission/selector';
import { fetchPermissions } from 'src/module/permission/action';
import { rolesNameToKey } from 'src/utils/utils';
import variables from 'src/style/variable/variables.module.scss';


export default function ApiKeysScreen () {
  const dispatch = useDispatch();
  const [isAddApiKeyModalOpen, setIsAddApiKeyModalOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const permissions = useSelector(getPermissions);

  const creationLoading = useSelector(getApiKeyCreationLoading);
  const newApiKeyToken = useSelector(getNewApiKeyToken);
  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, []);

  useEffect(() => {
    if (permissions) {
      const allRoles = [];
      permissions.map((perm) => {
        const roles = perm.get('role');
        roles?.map(role => {
          if (!allRoles.includes(role) && role != 'SmartTrace Admin' && role != 'Super Admin') {
            allRoles.push(role);
          }
        });
      });
      setAvailableRoles(allRoles);
    }
  }, [permissions]);

  const handleCreateApiKey = () => {
    dispatch(createApiKey(selectedRoles));
  };

  const handleCloseModal = () => {
    setIsAddApiKeyModalOpen(false);
    setSelectedRoles([]);
    // Clear the token from the store when modal is closed
    if (newApiKeyToken) {
      dispatch(clearApiKeyToken());
    }
  };

  const handleCopyToken = () => {
    if (newApiKeyToken) {
      navigator.clipboard.writeText(newApiKeyToken);
      toast.success('API key copied to clipboard');
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRoles(event.target.value);
  };

  return (
    <div id='api-keys-screen' className='api-keys-screen page'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper>
            <h2 >
              Manage API Keys
            </h2>
            <div className='inner-section'>
              <div className='select-device-bar'>
              </div>
              <div className='api-keys-list-container'>
                <ApiKeysList />
              </div>
              <div className='button-bar'>
                <Button cta backgroundColor='primary'
                  icon={<AddIcon />}
                  onClick={() => setIsAddApiKeyModalOpen(true)}
                >
                  Add API Key
                </Button>
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Modal
        open={isAddApiKeyModalOpen}
        onClose={handleCloseModal}
        aria-labelledby='api-key-modal-title'
      >
        <div className='modal api-keys-modal' style={{ minWidth: '500px', minHeight: '250px' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Create API Key
          </Typography>

          {!newApiKeyToken ? (
            <>
              <Box mt={3} mb={3}>
                <FormControl fullWidth>
                  <InputLabel>Select Roles (Optional)</InputLabel>
                  <Select
                    multiple
                    value={selectedRoles}
                    onChange={handleRoleChange}
                    renderValue={(selected) => (
                      <Box className='role-chips-container'>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" style={{ fontSize: '1.3rem' }} />
                        ))}
                      </Box>
                    )}
                  >
                    {availableRoles.map((role) => {
                      return (
                        <MenuItem key={role} value={rolesNameToKey(role)}>
                          {role}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  onClick={handleCloseModal}
                  disabled={creationLoading}
                >
                  Cancel
                </Button>
                <Button
                  cta
                  backgroundColor="primary"
                  onClick={handleCreateApiKey}
                  disabled={creationLoading}
                >
                  {creationLoading ? 'Generating...' : 'Generate API Key'}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                gutterBottom
                className='warning-text'
                style={{ color: variables.error, textAlign: 'center' }}
              >
              Copy this key now - you won&apos;t be able to see it again!
              </Typography>

              <Box mt={2} mb={3}>
                <TextField
                  fullWidth
                  label="Your New API Key"
                  value={newApiKeyToken}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={handleCopyToken} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                  variant="outlined"
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  cta
                  backgroundColor="primary"
                  onClick={handleCloseModal}
                >
                  Done
                </Button>
              </Box>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
